import { useCallback, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import useMediaQuery from "@mui/material/useMediaQuery";

import { endOfDay, isValid, startOfDay } from "date-fns";
import { noop } from "lodash";

import { useAppSelector } from "app/hooks";
import CustomFiltersList from "components/custom-filters-list";
import type { CustomFilterOption } from "components/custom-filters-list/custom-filters-list.component";
import DateRangeFilter from "components/date-range-filter";
import { NON_BREAKABLE_SPACE } from "constants/common";
import { selectCurrentClinic } from "features/auth/auth-slice";
import ReportDownloadBanner from "features/reports/components/report-download-banner/report-download-banner.component";
import { useCsvReportHook } from "features/reports/use-csv-report.hook";
import { useLazyDownloadStaffTaskCSVQuery } from "services/staff-tasks";
import type { StaffTasksFilter } from "services/types/staff-tasks.type";

import FilterSearchInput from "../../../components/filters-bar/filter-search-input.component";
import StaffTasksBulkActionsDropdown from "../components/staff-tasks-bulk-actions-dropdown/staff-tasks-bulk-actions-dropdown.component";
import type { PatientTasksCustomFiltersValues } from "./use-tasks-v2-custom-filters.hook";

const SMALL_SCREEN_MOST_COMON_FILTER_NAMES = new Set<string>([]);

const MID_SCREEN_MOST_COMON_FILTER_NAMES = new Set([
  "Assignee",
  `Created${NON_BREAKABLE_SPACE}By`,
  "Status"
]);

const NORMAL_SCREEN_MOST_COMON_FILTER_NAMES = new Set([
  "Assignee",
  `Created${NON_BREAKABLE_SPACE}By`,
  "Status",
  "Priority"
]);

const DEFAULT_DATE_INTERVAL_OPTIONS = [
  <ToggleButton value="overdue">Overdue</ToggleButton>,
  <ToggleButton value="today">Today</ToggleButton>,
  <ToggleButton value="week">WTD</ToggleButton>,
  <ToggleButton value="future">Future</ToggleButton>
];

type PatientTasksV2FiltersProps = {
  customFilters: CustomFilterOption[];
  currentFilters: StaffTasksFilter;
  selectedCustomFilters: PatientTasksCustomFiltersValues;
  setCurrentFilters: React.Dispatch<React.SetStateAction<StaffTasksFilter>>;
  updateDateRange: React.Dispatch<
    React.SetStateAction<{
      before_due_at: string | null;
      after_due_at: string | null;
    }>
  >;
  before_due_at: string | null;
  after_due_at: string | null;
  isModalMode?: boolean;
  enableGrouping?: boolean;
  selectedTaskIds?: number[];
  totalTasksCount?: number;
  setSelectedTaskIds?: React.Dispatch<React.SetStateAction<number[]>>;
  onRefreshData?: () => void;
  isSelectAllMode?: boolean;
  totalFilteredCount?: number;
  onCreateTask?: () => void;
};

export default function PatientTasksV2Filters({
  customFilters,
  currentFilters,
  selectedCustomFilters,
  setCurrentFilters,
  updateDateRange,
  before_due_at,
  after_due_at,
  isModalMode,
  enableGrouping = true,
  selectedTaskIds = [],
  totalTasksCount = 0,
  setSelectedTaskIds,
  onRefreshData,
  isSelectAllMode = false,
  totalFilteredCount = 0,
  onCreateTask
}: PatientTasksV2FiltersProps) {
  const isMidScreen = useMediaQuery("(min-width:1920px)");
  const isSmallScreen = useMediaQuery("(max-width:1512px)");

  const isCompactView = !enableGrouping && (isModalMode || isSmallScreen);
  const currentClinic = useAppSelector(selectCurrentClinic);

  const handleChangeStringFilter = useCallback(
    (value: string) => {
      setCurrentFilters((prev) => ({
        ...prev,
        search: value.trim() || undefined
      }));
    },
    [setCurrentFilters]
  );

  const handleClearStringFilter = useCallback(() => {
    setCurrentFilters((prev) => {
      const { search, ...rest } = prev;
      return rest;
    });
  }, [setCurrentFilters]);

  const { mostCommonFilters, remainingFilters } = useMemo(() => {
    const names = isCompactView
      ? SMALL_SCREEN_MOST_COMON_FILTER_NAMES
      : isMidScreen
        ? MID_SCREEN_MOST_COMON_FILTER_NAMES
        : NORMAL_SCREEN_MOST_COMON_FILTER_NAMES;

    const mostCommonFilters = customFilters.filter((item) =>
      names.has(item.label)
    );
    const remainingFilters = customFilters.filter(
      (item) => !names.has(item.label)
    );

    return { mostCommonFilters, remainingFilters };
  }, [customFilters, isCompactView, isMidScreen]);

  const { csvReport, csvReportIsReady, setCsvReport } = useCsvReportHook({
    currentFilters
  });

  const [downloadStaffTasksCsv, { isFetching }] =
    useLazyDownloadStaffTaskCSVQuery();

  const handleDownloadCSV = useCallback(() => {
    downloadStaffTasksCsv({
      ...currentFilters,
      current_clinic: currentClinic?.id
    }).then(
      ({ data: csvResponse }) => csvResponse && setCsvReport(csvResponse)
    );
  }, [currentClinic?.id, currentFilters, downloadStaffTasksCsv, setCsvReport]);

  return (
    <Stack spacing={2} flexWrap="wrap">
      {csvReport && (
        <ReportDownloadBanner
          csvReport={{ ...csvReport, isReady: csvReportIsReady }}
          closeReport={() => setCsvReport(null)}
          reportName="Tasks"
        />
      )}

      <Stack
        columnGap={2}
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box pt={3.5}>
            <CustomFiltersList
              mostCommonFilters={mostCommonFilters}
              filters={remainingFilters}
              moreFiltersLabel="Filters"
              selectedFilters={selectedCustomFilters}
              wrap
            />
          </Box>
          <Box
            sx={{
              maxWidth: !isMidScreen ? "385px" : undefined
            }}
          >
            <DateRangeFilter
              sx={{
                minWidth: 0
              }}
              fromDate={after_due_at ? new Date(after_due_at) : null}
              toDate={before_due_at ? new Date(before_due_at) : null}
              selectedRange=""
              hideMoreOptions
              sxInputField={{ width: "180px" }}
              defaultDateIntervalOptions={DEFAULT_DATE_INTERVAL_OPTIONS}
              onChange={(fromDate, toDate) => {
                const from_date_start =
                  fromDate && (isValid(fromDate) ? startOfDay(fromDate) : null);
                const to_date_end =
                  toDate && (isValid(toDate) ? endOfDay(toDate) : null);
                if (
                  (from_date_start?.toISOString() || null) !== before_due_at ||
                  (to_date_end?.toISOString() || null) !== after_due_at
                ) {
                  updateDateRange({
                    after_due_at: from_date_start
                      ? from_date_start.toISOString()
                      : null,
                    before_due_at: to_date_end
                      ? to_date_end.toISOString()
                      : null
                  });
                }
              }}
              label="Due From"
              allowDebounce
            />
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
            <FilterSearchInput
              searchPlaceholder="Search tasks by title or description"
              searchString={currentFilters.search || ""}
              isEditMode={true}
              onChange={handleChangeStringFilter}
              onChipClick={noop}
              onApply={handleChangeStringFilter}
              onClear={handleClearStringFilter}
              sx={{ minWidth: 270 }}
            />
            {enableGrouping && (
              <Box>
                <InputLabel id="group-by">Group&nbsp;By</InputLabel>
                <RadioGroup
                  value={Boolean(currentFilters.group_by_priority)}
                  onChange={(e) => {
                    const isGroupByPriority = e.target.value === "true";
                    setCurrentFilters((prev) => ({
                      ...prev,
                      group_by_priority: isGroupByPriority
                    }));
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row"
                  }}
                >
                  <FormControlLabel
                    value={true}
                    control={<Radio />}
                    label="Priority"
                  />

                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="None"
                  />
                </RadioGroup>
              </Box>
            )}
            {!enableGrouping && (
              <>
                {setSelectedTaskIds && (
                  <StaffTasksBulkActionsDropdown
                    selectedTaskIds={selectedTaskIds}
                    totalTasksCount={totalTasksCount}
                    onTaskSelectionChange={setSelectedTaskIds}
                    onRefreshData={onRefreshData}
                    isSelectAllMode={isSelectAllMode}
                    totalFilteredCount={totalFilteredCount}
                    currentFilters={currentFilters}
                  />
                )}

                <LoadingButton
                  variant="reportDownload"
                  size="small"
                  loading={isFetching}
                  onClick={handleDownloadCSV}
                >
                  Download CSV
                </LoadingButton>
              </>
            )}
          </Stack>
        </Stack>
        {onCreateTask && (
          <Box sx={{ mt: 3, flexShrink: 0 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onCreateTask}
            >
              Create Task
            </Button>
          </Box>
        )}
      </Stack>
    </Stack>
  );
}
